import { subscribeToSlot } from "../api/stream";
import type { StreamSlot } from "../types/stream";
import logger from "./logger";

export type WhepState =
  | "idle"
  | "connecting"
  | "playing"
  | "reconnecting"
  | "ended"
  | "error";

export interface WhepSession {
  readonly slotId: number;
  readonly stream: MediaStream;
  getState(): WhepState;
  getError(): string | undefined;
  subscribe(listener: () => void): () => void;
  close(): void;
}

const ICE_GATHER_TIMEOUT_MS = 10_000;
const RECOVERY_TIMEOUT_MS = 15_000;

/**
 * Owns one WHEP subscription. Deliberately free of React: the store keeps a
 * session alive across route changes, long after the component that first
 * asked for it has unmounted.
 *
 * Only a slot's identity is needed to open a session — its id to subscribe and
 * its content type to choose the transceivers — so callers can pass a
 * `SlotIdentity` rather than a full, possibly stale, `StreamSlot`.
 */
export function createWhepSession(
  slot: Pick<StreamSlot, "id" | "streamContentType">,
): WhepSession {
  const stream = new MediaStream();
  const listeners = new Set<() => void>();
  let pc: RTCPeerConnection | null = null;
  let state: WhepState = "connecting";
  let error: string | undefined;
  let closed = false;
  let recoveryTimer: ReturnType<typeof setTimeout> | null = null;

  function emit() {
    for (const listener of [...listeners]) listener();
  }

  function setState(next: WhepState, nextError?: string) {
    if (state === next && error === nextError) return;
    state = next;
    error = nextError;
    emit();
  }

  function clearRecoveryTimer() {
    if (recoveryTimer === null) return;
    clearTimeout(recoveryTimer);
    recoveryTimer = null;
  }

  /** A blip: give the connection a grace period before declaring the stream over. */
  function beginRecovery() {
    if (closed || state === "ended" || recoveryTimer !== null) return;
    setState("reconnecting");
    recoveryTimer = setTimeout(() => {
      recoveryTimer = null;
      if (closed) return;
      logger.info("[WHEP] Slot", slot.id, "did not recover — ending session");
      setState("ended");
    }, RECOVERY_TIMEOUT_MS);
  }

  function endRecovery() {
    if (closed || state === "ended") return;
    clearRecoveryTimer();
    setState("playing");
  }

  function watchTrack(track: MediaStreamTrack) {
    track.onended = () => {
      if (closed) return;
      const live = stream
        .getTracks()
        .some((t) => t.readyState !== "ended");
      if (!live) {
        clearRecoveryTimer();
        setState("ended");
      }
    };
    track.onmute = () => beginRecovery();
    track.onunmute = () => endRecovery();
  }

  function waitForIceGathering(peer: RTCPeerConnection): Promise<void> {
    return new Promise<void>((resolve) => {
      if (peer.iceGatheringState === "complete") {
        resolve();
        return;
      }
      const timer = setTimeout(() => {
        logger.warn(
          "[WHEP] ICE gathering timed out after 10s, proceeding with partial candidates",
        );
        resolve();
      }, ICE_GATHER_TIMEOUT_MS);
      peer.onicegatheringstatechange = () => {
        if (peer.iceGatheringState === "complete") {
          clearTimeout(timer);
          resolve();
        }
      };
    });
  }

  async function connect() {
    try {
      const { whepUrl } = await subscribeToSlot(slot.id);
      if (closed) return;
      logger.info("[WHEP] Got WHEP URL:", whepUrl);

      const peer = new RTCPeerConnection();
      pc = peer;
      if (closed) {
        peer.close();
        pc = null;
        return;
      }

      // Set ontrack BEFORE setRemoteDescription to avoid missing early events.
      // Tracks are added to a MediaStream we built upfront — event.streams[0]
      // can be empty when the server does not bundle tracks into a stream.
      peer.ontrack = (event) => {
        if (closed) return;
        logger.info("[WHEP] Track received:", event.track.kind);
        stream.addTrack(event.track);
        watchTrack(event.track);
        // Tell listeners about every track, not only the first: an element
        // bound to the stream must be re-bound for the new track to play.
        if (state === "playing") emit();
        else setState("playing");
      };

      peer.onconnectionstatechange = () => {
        if (closed) return;
        const connectionState = peer.connectionState;
        logger.info("[WHEP] Connection state:", connectionState);
        if (connectionState === "failed" || connectionState === "closed") {
          clearRecoveryTimer();
          setState("ended");
        } else if (connectionState === "disconnected") {
          beginRecovery();
        } else if (connectionState === "connected") {
          endRecovery();
        }
      };

      if (slot.streamContentType === "AUDIO") {
        peer.addTransceiver("audio", { direction: "recvonly" });
      } else {
        peer.addTransceiver("video", { direction: "recvonly" });
        peer.addTransceiver("audio", { direction: "recvonly" });
      }

      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      await waitForIceGathering(peer);
      if (closed) return;

      const res = await fetch(whepUrl, {
        method: "POST",
        headers: { "Content-Type": "application/sdp" },
        body: peer.localDescription!.sdp,
      });
      logger.info("[WHEP] WHEP response status:", res.status);
      if (!res.ok) throw new Error(`WHEP error ${res.status}`);
      if (closed) return;

      const answerSdp = await res.text();
      await peer.setRemoteDescription({ type: "answer", sdp: answerSdp });
      logger.info("[WHEP] Remote description set — waiting for tracks...");
    } catch (err) {
      if (closed) return;
      const message = err instanceof Error ? err.message : "Connection failed";
      logger.error("[WHEP] Connection error:", message);
      setState("error", message);
    }
  }

  connect();

  return {
    slotId: slot.id,
    stream,
    getState: () => state,
    getError: () => error,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    close() {
      if (closed) return;
      closed = true;
      logger.info("[WHEP] Closing session for slot", slot.id);
      clearRecoveryTimer();
      pc?.close();
      pc = null;
      setState("idle");
      listeners.clear();
    },
  };
}
